import React, { FunctionComponent, ReactNode, useCallback, useMemo } from 'react'
import { ImageSourcePropType, StyleProp, ViewStyle } from 'react-native'

import { CSSObject } from 'styled-components'
import styled, { useTheme } from 'styled-components/native'

import { getBreakpointValue } from '@lyrahealth-inc/shared-app-logic/src/features/common/constants/theme/breakpoints'

import { BodyText } from '../../atoms/bodyText/BodyText'
import { ArrowIcon } from '../../atoms/icons/ArrowIcon'
import { Image } from '../../atoms/image/Image'
import { PressableOpacity } from '../../atoms/pressableOpacity/PressableOpacity'
import { PrimaryButton } from '../../atoms/primaryButton/PrimaryButton'
import { Title } from '../../atoms/title/Title'
import { IS_WEB } from '../../constants'
import { BodyTextSize, TitleSize } from '../../styles/typeStyles'
import { ThemeType } from '../../utils/themes/ThemeProvider'
import { tID } from '../../utils/utils'

interface StyleBreakpointValues {
  mobile?: CSSObject
  default?: CSSObject
}

const CardContainer = styled.View<{ theme: ThemeType; cardContainerStyle?: StyleBreakpointValues }>(
  ({ theme, cardContainerStyle }) => ({
    width: '100%',
    borderRadius: 24,
    overflow: 'hidden',
    flexDirection: getBreakpointValue(theme.breakpoints, {
      isMobileSized: 'column',
      default: 'row',
    }),
    maxWidth: getBreakpointValue(theme.breakpoints, {
      isMobileSized: cardContainerStyle?.mobile?.maxWidth ?? 312,
      default: cardContainerStyle?.default?.maxWidth ?? 870,
    }),
    height: getBreakpointValue(theme.breakpoints, {
      isMobileSized: cardContainerStyle?.mobile?.height ?? 410,
      default: cardContainerStyle?.default?.height ?? 400,
    }),
  }),
)

const ImageContainer = styled.View<{
  theme: ThemeType
  imageContainerStyle?: StyleBreakpointValues
  clipToFitContent?: boolean
}>(({ theme, imageContainerStyle, clipToFitContent = false }) => ({
  maxWidth: getBreakpointValue(theme.breakpoints, {
    isMobileSized: imageContainerStyle?.mobile?.maxWidth ?? 312,
    default: imageContainerStyle?.default?.maxWidth ?? 389,
  }),
  ...(theme.breakpoints.isMobileSized && { flex: 1 }),
  ...(!theme.breakpoints.isMobileSized && clipToFitContent && { flexShrink: 1, overflow: 'hidden' }),
  ...(theme.breakpoints.isMobileSized && imageContainerStyle?.mobile),
  ...(!theme.breakpoints.isMobileSized && imageContainerStyle?.default),
}))

const StyledImage = styled(Image)<{ imageWidth?: number | string }>(({ imageWidth }) => ({
  width: imageWidth ?? '100%',
  height: '100%',
  ...(IS_WEB && { objectFit: 'cover' }),
}))

const ContentContainer = styled.View<{
  theme: ThemeType
  hasButtonText?: boolean
  contentContainerStyle?: StyleBreakpointValues
  clipImageToFitContent?: boolean
}>(({ theme, hasButtonText, contentContainerStyle, clipImageToFitContent = false }) => ({
  flex: 1,
  justifyContent: hasButtonText ? 'center' : 'space-between',
  ...(contentContainerStyle?.default?.width && {
    width: getBreakpointValue(theme.breakpoints, {
      isMobileSized: 'auto',
      default: contentContainerStyle.default.width,
    }),
  }),
  height: getBreakpointValue(theme.breakpoints, {
    isMobileSized: '180px',
    default: 'auto',
  }),
  padding: getBreakpointValue(theme.breakpoints, {
    isMobileSized: contentContainerStyle?.mobile?.padding ?? theme.spacing['16px'],
    default: contentContainerStyle?.default?.padding ?? theme.spacing['40px'],
  }),
  backgroundColor: contentContainerStyle?.default?.backgroundColor ?? theme.colors.backgroundTransparent,
  ...(!theme.breakpoints.isMobileSized && clipImageToFitContent && { minWidth: 350, flexShrink: 0 }),
  ...(theme.breakpoints.isMobileSized && contentContainerStyle?.mobile),
  ...(!theme.breakpoints.isMobileSized && contentContainerStyle?.default),
}))

const TextContainer = styled.View({
  flex: 1,
  justifyContent: 'center',
})

const DescriptionContainer = styled.View<{ theme: ThemeType; descriptionContainerStyle?: StyleBreakpointValues }>(
  ({ theme, descriptionContainerStyle }) => ({
    marginTop: getBreakpointValue(theme.breakpoints, {
      isMobileSized: descriptionContainerStyle?.mobile?.marginTop ?? theme.spacing['8px'],
      default: descriptionContainerStyle?.default?.marginTop ?? theme.spacing['12px'],
    }),
    marginBottom: getBreakpointValue(theme.breakpoints, {
      isMobileSized: descriptionContainerStyle?.mobile?.marginBottom ?? theme.spacing['16px'],
      default: descriptionContainerStyle?.default?.marginBottom ?? theme.spacing['32px'],
    }),
  }),
)

const ArrowButtonContainer = styled(PressableOpacity)<{ theme: ThemeType }>(({ theme }) => ({
  alignSelf: 'flex-end',
  borderRadius: '50%',
  backgroundColor: theme.colors.primaryButtonBackgroundDefault,
  width: getBreakpointValue(theme.breakpoints, {
    isMobileSized: theme.spacing['40px'],
    default: theme.spacing['56px'],
  }),
  height: getBreakpointValue(theme.breakpoints, {
    isMobileSized: theme.spacing['40px'],
    default: theme.spacing['56px'],
  }),
}))

const StyledArrowIcon = styled(ArrowIcon)({
  margin: 'auto',
})

export interface SplitHeroCardProps {
  /** Title text to display - can be a string or FormattedMessage */
  title: ReactNode
  /** Description text - can be a string or FormattedMessage */
  description?: ReactNode
  /** Image source - can be a URI string, require() result, or ImageSourcePropType */
  imageSource: string | number | ImageSourcePropType
  /** Button text - if provided, shows a full-width button instead of arrow */
  buttonText?: ReactNode
  /** Callback when button/arrow is pressed */
  onPress: () => void
  /** Test ID prefix for the component */
  testID?: string
  /** Additional style for the card container */
  style?: StyleProp<ViewStyle>
  /** Custom CTA section - if provided, replaces the default button/arrow */
  ctaSection?: ReactNode
  isLargerTitleSmallerDescription?: boolean
  customStyles?: {
    imageContainerStyle?: StyleBreakpointValues
    cardContainerStyle?: StyleBreakpointValues
    descriptionContainerStyle?: StyleBreakpointValues
    contentContainerStyle?: StyleBreakpointValues
  }
  fontColors?: {
    title?: string
    description?: string
  }
  imageWidth?: number | string
  customTitleSize?: BodyTextSize | TitleSize
  customDescriptionSize?: BodyTextSize | TitleSize
  clipImageToFitContent?: boolean
}

export const SplitHeroCard: FunctionComponent<SplitHeroCardProps> = ({
  title,
  description,
  imageSource,
  buttonText,
  onPress,
  testID = 'SplitHeroCard',
  style,
  ctaSection,
  isLargerTitleSmallerDescription = false,
  customStyles = {},
  fontColors = {},
  imageWidth,
  customTitleSize,
  customDescriptionSize,
  clipImageToFitContent = false,
}) => {
  const {
    colors,
    breakpoints: { isMobileSized },
  } = useTheme() as ThemeType

  const { imageContainerStyle, cardContainerStyle, descriptionContainerStyle, contentContainerStyle } = customStyles

  const hasButtonText = Boolean(buttonText)
  const hasCtaSection = Boolean(ctaSection)
  const TextContainerWrapper = hasButtonText || hasCtaSection ? React.Fragment : TextContainer

  const renderCtaSection = useCallback(() => {
    // Custom CTA section takes precedence
    if (ctaSection) {
      return ctaSection
    }
    // Default: button or arrow
    if (hasButtonText) {
      return <PrimaryButton text={buttonText} onPress={onPress} testID={tID(`${testID}-button`)} fullWidth />
    }
    return (
      <ArrowButtonContainer onPress={onPress} testID={tID(`${testID}-arrowButton`)}>
        <StyledArrowIcon fillColor={colors.primaryButtonTextActive} />
      </ArrowButtonContainer>
    )
  }, [buttonText, ctaSection, hasButtonText, onPress, testID, colors.primaryButtonTextActive])

  const titleSize = useMemo(() => {
    if (customTitleSize) {
      return customTitleSize
    }
    if (isLargerTitleSmallerDescription) {
      return isMobileSized ? TitleSize.SMALL : TitleSize.MEDIUM
    }
    return isMobileSized ? BodyTextSize.DEFAULT : BodyTextSize.LARGE
  }, [customTitleSize, isLargerTitleSmallerDescription, isMobileSized])

  const descriptionSize = useMemo(() => {
    if (customDescriptionSize) {
      return customDescriptionSize
    }
    if (isLargerTitleSmallerDescription) {
      return isMobileSized ? BodyTextSize.DEFAULT : BodyTextSize.LARGE
    }
    return isMobileSized ? TitleSize.MEDIUM : TitleSize.LARGE
  }, [customDescriptionSize, isLargerTitleSmallerDescription, isMobileSized])

  return (
    <CardContainer testID={tID(testID)} style={style} cardContainerStyle={cardContainerStyle}>
      <ImageContainer
        imageContainerStyle={imageContainerStyle}
        clipToFitContent={clipImageToFitContent}
        pointerEvents='none'
      >
        <StyledImage source={imageSource} testID={tID(`${testID}-image`)} imageWidth={imageWidth} contentFit='cover' />
      </ImageContainer>
      <ContentContainer
        hasButtonText={hasButtonText || hasCtaSection}
        contentContainerStyle={contentContainerStyle}
        clipImageToFitContent={clipImageToFitContent}
      >
        <TextContainerWrapper>
          {isLargerTitleSmallerDescription ? (
            <Title
              text={title}
              size={titleSize as TitleSize}
              color={fontColors.title ?? colors.textPrimary}
              testID={tID(`${testID}-title`)}
            />
          ) : (
            <BodyText
              text={title}
              size={titleSize as BodyTextSize}
              color={fontColors.title ?? colors.textPrimary}
              testID={tID(`${testID}-title`)}
            />
          )}
          {description && (
            <DescriptionContainer descriptionContainerStyle={descriptionContainerStyle}>
              {isLargerTitleSmallerDescription ? (
                <BodyText
                  text={description}
                  size={descriptionSize as BodyTextSize}
                  color={fontColors.description ?? colors.textPrimary}
                  testID={tID(`${testID}-description`)}
                />
              ) : (
                <Title
                  text={description}
                  size={descriptionSize as TitleSize}
                  color={fontColors.description ?? colors.textPrimary}
                  testID={tID(`${testID}-description`)}
                />
              )}
            </DescriptionContainer>
          )}
        </TextContainerWrapper>
        {renderCtaSection()}
      </ContentContainer>
    </CardContainer>
  )
}
